import * as React from 'react';
import classNames from 'classnames';
import { usePopperTooltip } from 'react-popper-tooltip';
import { CSSTransition } from 'react-transition-group';

import s from './Tooltip.sss';

type Placement = 'auto'
| 'auto-start'
| 'auto-end'
| 'top'
| 'top-start'
| 'top-end'
| 'bottom'
| 'bottom-start'
| 'bottom-end'
| 'right'
| 'right-start'
| 'right-end'
| 'left'
| 'left-start'
| 'left-end';

type Theme = 'white' | 'black';
type Trigger = 'click' | 'right-click' | 'hover' | 'focus';

export interface Props {
  children: React.ReactElement<unknown, string>;
  content: React.ReactNode;
  trigger?: Trigger | Trigger[];
  placement?: Placement;
  theme?: Theme;
  padding?: string | number;
  width?: string | number;
  zIndex?: string | number;
  isInteractive?: boolean;
  isDefaultVisible?: boolean;
  isCloseOnOutsideClick?: boolean;
  isCloseOnTriggerHidden?: boolean;
  isVisible?: boolean;
  isFollowCursor?: boolean;
  isShowArrow?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  delayHide?: number;
  delayShow?: number;
  offset?: [number, number];
}

export const Tooltip: React.FC<Props> = ({
  children,
  content,
  isVisible,
  onVisibleChange,
  isCloseOnOutsideClick = true,
  isCloseOnTriggerHidden = true,
  isDefaultVisible = false,
  isFollowCursor = false,
  isInteractive = false,
  isShowArrow = true,
  delayHide = 0,
  delayShow = 0,
  trigger = 'hover',
  placement = 'auto',
  theme = 'white',
  offset = [0, 12],
  padding = 16,
  width = 'auto',
  zIndex = 900,
}) => {
  const rootRef = React.useRef<HTMLDivElement | null>(null)

  const {
    getArrowProps,
    getTooltipProps,
    setTooltipRef,
    setTriggerRef,
    visible,
  } = usePopperTooltip({
    trigger,
    placement,
    delayHide,
    delayShow,
    onVisibleChange,
    offset,
    defaultVisible: isDefaultVisible,
    closeOnOutsideClick: isCloseOnOutsideClick,
    closeOnTriggerHidden: isCloseOnTriggerHidden,
    visible: isVisible,
    followCursor: isFollowCursor,
    interactive: isInteractive,
  });

  return (
    <>
      {/* @ts-ignore */}
      {React.cloneElement(children, { ref: setTriggerRef })}

      <CSSTransition
        classNames={s}
        timeout={166}
        in={visible}
        unmountOnExit={true}
        nodeRef={rootRef}
      >
        <div
          ref={(r) => {
            setTooltipRef(r);
            rootRef.current = r;
          }}
          {...getTooltipProps({
            className: classNames(s.root, s[theme]),
            style: { width, zIndex },
          })}
        >
          <div className={classNames(s.animation)}>
            {isShowArrow && (
              <div {...getArrowProps({ className: s.arrow })} />
            )}
            <div className={s.content} style={{ padding }}>
              {content}
            </div>
          </div>
        </div>
      </CSSTransition>
    </>
  );
};
